import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import Footer from '../components/Footer';
import WorkMedia from '../components/WorkMedia';
import { PlaceholderImage } from '../components/PlaceholderMedia';
import workData from '../data/work.json';
import siteData from '../data/site.json';
import { SiInstagram, SiLinkedin, SiThreads, SiGithub } from 'react-icons/si';

const socialIcons = {
  Instagram: SiInstagram,
  LinkedIn: SiLinkedin,
  Threads: SiThreads,
  Github: SiGithub,
};

// Helper to detect media type from URL
const getMediaType = (url) => {
  if (!url) return 'image';
  const lower = url.toLowerCase();
  if (lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov')) {
    return 'video';
  }
  if (lower.includes('youtube.com') || lower.includes('youtu.be')) {
    return 'youtube';
  }
  return 'image';
};

// Extract YouTube video ID from URL
const getYouTubeId = (url) => {
  if (!url) return null;
  // Handle youtu.be/VIDEO_ID
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) return shortMatch[1];
  // Handle youtube.com/watch?v=VIDEO_ID
  const longMatch = url.match(/[?&]v=([^?&]+)/);
  if (longMatch) return longMatch[1];
  // Handle youtube.com/embed/VIDEO_ID
  const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
  if (embedMatch) return embedMatch[1];
  return null;
};

// Render copy with @mentions turned into Instagram links (newlines kept via
// the pre-line container). The lookbehind avoids matching @ inside emails/URLs.
const renderExcerpt = (text) => {
  if (!text) return text;
  return text.split(/((?<![\w.])@[\w.]+)/g).map((part, i) =>
    /^@[\w.]+$/.test(part) ? (
      <a
        key={i}
        href={`https://www.instagram.com/${part.slice(1)}/`}
        target="_blank"
        rel="noreferrer"
        style={{ color: 'inherit', textDecoration: 'underline' }}
      >
        {part}
      </a>
    ) : (
      part
    )
  );
};

// Gallery video that plays only when visible, with placeholder
const GalleryVideo = ({ src }) => {
  const videoRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={`media-placeholder image-cover ${loaded ? 'media-placeholder--loaded' : ''}`}>
      <video
        ref={videoRef}
        src={src}
        muted
        loop
        playsInline
        onLoadedData={() => setLoaded(true)}
      />
    </div>
  );
};

// YouTube thumbnail with fallback from maxres to mqdefault, with placeholder
const YouTubeThumbnail = ({ videoId }) => {
  const [src, setSrc] = useState(`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`);
  const [loaded, setLoaded] = useState(false);
  const fallbackSrc = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  
  const handleLoad = (e) => {
    // YouTube returns a 120x90 placeholder when maxres isn't available
    if (e.target.naturalWidth <= 120 && src !== fallbackSrc) {
      setSrc(fallbackSrc);
      return;
    }
    setLoaded(true);
  };
  
  return (
    <div className={`media-placeholder youtube-thumbnail-image ${loaded ? 'media-placeholder--loaded' : ''}`}>
      <img
        loading="lazy"
        src={src}
        alt=""
        onLoad={handleLoad}
        onError={() => setSrc(fallbackSrc)}
      />
    </div>
  );
};

const WorkDetail = () => {
  const { slug } = useParams();
  const projectIndex = workData.findIndex((item) => item.slug === slug);
  const project = projectIndex >= 0 ? workData[projectIndex] : null;
  const nextProject = project ? workData[(projectIndex + 1) % workData.length] : null;
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const galleryImages = project?.gallery || [];

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setLightboxIndex(null);
      }
      if (event.key === 'ArrowRight') {
        setLightboxIndex((current) => {
          if (current === null || !galleryImages.length) {
            return current;
          }
          return (current + 1) % galleryImages.length;
        });
      }
      if (event.key === 'ArrowLeft') {
        setLightboxIndex((current) => {
          if (current === null || !galleryImages.length) {
            return current;
          }
          return (current - 1 + galleryImages.length) % galleryImages.length;
        });
      }
    };

    if (lightboxIndex !== null) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxIndex, galleryImages.length]);

  if (!project) {
    return (
      <main className="main-wrapper">
        <div className="main-wrapper-left"></div>
        <div className="main-wrapper-right">
          <section className="project-about">
            <div className="project-about-left">
              <h1 className="heading-style-h1">Project not found</h1>
              <div className="max-width-medium">
                <div>We couldn’t find that project. Please return to the work list.</div>
              </div>
            </div>
          </section>
          <Footer />
        </div>
      </main>
    );
  }

  const activeLightboxItem = lightboxIndex !== null ? galleryImages[lightboxIndex] : null;
  const activeLightboxImage = activeLightboxItem
    ? (typeof activeLightboxItem === 'string' ? activeLightboxItem : activeLightboxItem.url)
    : null;

  return (
    <main className="main-wrapper">
      <div className="main-wrapper-left">
        <div className="project-hero-image-wrapper">
          <div className="card-corner-bottom-right">
            <div className="text-meta">{project.category}</div>
            <div className="corner-bottom-right top w-embed">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
              </svg>
            </div>
            <div className="corner-bottom-right bottom w-embed">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
              </svg>
            </div>
          </div>
          <WorkMedia media={project.media} className="project-hero-image" />
        </div>
      </div>
      <div className="main-wrapper-right">
        <section className="project-about">
          <div className="project-about-left">
            <h1 className="heading-style-h1">{project.title}</h1>
            <div className="max-width-medium">
              <div style={{ whiteSpace: 'pre-line' }}>{renderExcerpt(project.excerpt)}</div>
            </div>
            {project.credits?.length ? (
              <div className="max-width-medium project-credits">
                {project.credits.map((credit) => (
                  <div key={credit.role}>
                    <strong>{credit.role}:</strong> {credit.name}
                  </div>
                ))}
              </div>
            ) : null}
            {project.url && (
              <a href={project.url} target="_blank" rel="noreferrer" className="card-corner-bottom-right">
                <div className="text-meta">Link</div>
                <div className="corner-bottom-right top w-embed">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
                  </svg>
                </div>
                <div className="corner-bottom-right bottom w-embed">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
                  </svg>
                </div>
              </a>
            )}
          </div>
          <div className="project-about-right">
            <div className="project-about-card">
              <div className="text-meta text-color-muted">Year</div>
              <div className="text-meta">{project.year}</div>
            </div>
            <div className="project-about-card">
              <div className="text-meta text-color-muted">Category</div>
              <div className="text-meta">{project.category}</div>
            </div>
            <div className="project-about-card">
              <div className="text-meta text-color-muted">Platform</div>
              <div className="text-meta">{project.location}</div>
            </div>
            <div className="project-about-spacer"></div>
          </div>
        </section>

        {galleryImages.length ? (
          <div className="project-gallery">
            <div className={`project-gallery-grid${project.galleryLayout === 'two-column' ? ' project-gallery-grid-two-col' : ''}`}>
              {galleryImages.map((mediaItem, index) => {
                // Support both string URLs and objects with url/poster
                const mediaSrc = typeof mediaItem === 'string' ? mediaItem : mediaItem.url;
                const customPoster = typeof mediaItem === 'object' ? mediaItem.poster : null;
                const mediaType = getMediaType(mediaSrc);
                return (
                  <button
                    key={`${project.slug}-gallery-${index}`}
                    type="button"
                    className="project-gallery-image-wrapper"
                    onClick={() => setLightboxIndex(index)}
                  >
                    {mediaType === 'video' ? (
                      <GalleryVideo src={mediaSrc} />
                    ) : mediaType === 'youtube' ? (
                      <div className="youtube-thumbnail-wrapper image-cover">
                        {customPoster ? (
                          <PlaceholderImage loading="lazy" src={customPoster} alt="" className="youtube-thumbnail-image" />
                        ) : (
                          <YouTubeThumbnail videoId={getYouTubeId(mediaSrc)} />
                        )}
                        <div className="youtube-play-icon">
                          <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 5.14v14l11-7-11-7z" />
                          </svg>
                        </div>
                      </div>
                    ) : (
                      <PlaceholderImage loading="lazy" src={mediaSrc} alt="" className="image-cover" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {nextProject ? (
          <section className="project-next-section">
            <Link to={`/work/${nextProject.slug}`} className="project-next-card w-inline-block">
              <div className="project-next-card-content">
                <div className="label">
                  <div className="label-text">Next Project</div>
                </div>
                <div className="project-next-card-info">
                  <h2 className="heading-style-h2">{nextProject.title}</h2>
                  <div className="text-meta text-color-muted">
                    {nextProject.category} · {nextProject.year}
                  </div>
                </div>
                <div className="button-text">
                  <div className="button-text-inner">
                    <div className="button-text-text">View Project</div>
                    <div className="project-next-card-arrow w-embed">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M198,64V168a6,6,0,0,1-12,0V78.48L68.24,196.24a6,6,0,0,1-8.48-8.48L177.52,70H88a6,6,0,0,1,0-12H192A6,6,0,0,1,198,64Z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="button-text-line"></div>
                </div>
              </div>
              <div className="project-next-card-media">
                <PlaceholderImage className="project-next-card-image" src={nextProject.media.poster} alt={nextProject.media.alt || ''} loading="lazy" />
              </div>
            </Link>
          </section>
        ) : null}

        <section className="section-socials">
          {siteData.socials.map((social) => {
            const IconComponent = socialIcons[social.label];
            return (
              <a key={`work-${social.label}`} href={social.url} target="_blank" rel="noreferrer" className="social-card w-inline-block">
                <div className="text-meta">{social.label}</div>
                <div className="social-card-icon-wrapper">
                  <div className="social-card-icon w-embed">
                    {IconComponent ? <IconComponent /> : null}
                  </div>
                  <div className="social-card-icon-hover w-embed">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256">
                      <path d="M198,64V168a6,6,0,0,1-12,0V78.48L68.24,196.24a6,6,0,0,1-8.48-8.48L177.52,70H88a6,6,0,0,1,0-12H192A6,6,0,0,1,198,64Z"></path>
                    </svg>
                  </div>
                </div>
              </a>
            );
          })}
          <Link to="/contact" className="social-card primary w-inline-block">
            <div className="text-meta text-color-inverse">Contact Me</div>
            <div className="social-card-icon-wrapper">
              <div className="social-card-icon w-embed">
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256">
                  <path d="M224,50H32a6,6,0,0,0-6,6V192a14,14,0,0,0,14,14H216a14,14,0,0,0,14-14V56A6,6,0,0,0,224,50Zm-96,85.86L47.42,62H208.58ZM101.67,128,38,186.36V69.64Zm8.88,8.14L124,148.42a6,6,0,0,0,8.1,0l13.4-12.28L208.58,194H47.43ZM154.33,128,218,69.64V186.36Z"></path>
                </svg>
              </div>
              <div className="social-card-icon-hover w-embed">
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256">
                  <path d="M198,64V168a6,6,0,0,1-12,0V78.48L68.24,196.24a6,6,0,0,1-8.48-8.48L177.52,70H88a6,6,0,0,1,0-12H192A6,6,0,0,1,198,64Z"></path>
                </svg>
              </div>
            </div>
          </Link>
        </section>

        <Footer />
      </div>
      {activeLightboxImage ? (
        <div className="lightbox-overlay" role="dialog" aria-modal="true" onClick={() => setLightboxIndex(null)}>
          <button
            type="button"
            className="lightbox-arrow lightbox-arrow-left"
            aria-label="Previous image"
            onClick={(event) => {
              event.stopPropagation();
              setLightboxIndex((current) => (current - 1 + galleryImages.length) % galleryImages.length);
            }}
          >
            ‹
          </button>
          {(() => {
            const mediaType = getMediaType(activeLightboxImage);
            if (mediaType === 'video') {
              return (
                <video
                  key={activeLightboxImage}
                  src={activeLightboxImage}
                  className="lightbox-media"
                  autoPlay
                  loop
                  playsInline
                  controls
                  onClick={(event) => event.stopPropagation()}
                />
              );
            }
            if (mediaType === 'youtube') {
              const videoId = getYouTubeId(activeLightboxImage);
              return (
                <iframe
                  key={activeLightboxImage}
                  className="lightbox-media lightbox-youtube"
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
                  title="YouTube video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  onClick={(event) => event.stopPropagation()}
                />
              );
            }
            return (
              <img
                src={activeLightboxImage}
                alt=""
                className="lightbox-image"
                onClick={(event) => event.stopPropagation()}
              />
            );
          })()}
          <button
            type="button"
            className="lightbox-arrow lightbox-arrow-right"
            aria-label="Next image"
            onClick={(event) => {
              event.stopPropagation();
              setLightboxIndex((current) => (current + 1) % galleryImages.length);
            }}
          >
            ›
          </button>
        </div>
      ) : null}
    </main>
  );
};

export default WorkDetail;

